<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
    @keydown.esc.prevent="close()"
  >
    <el-overlay-loading :loading="saving" />
    <v-card>
      <v-card-title class="modalTitle pt-2 pb-1">
        <h3>{{ editId ? $t('Redaguoti lauką') : $t('Naujas laukas') }}</h3>
      </v-card-title>
      <v-card-text class="pt-2">
        <form @keydown.enter.prevent="submit()">
        <v-row>
          <v-col cols="12" md="4" class="pb-0">
            <el-text-field
              label="Lauko raktas"
              v-model="form.field_key"
              :maxlength="20"
              required
            />
          </v-col>
          <v-col cols="12" md="4" class="pb-0">
            <el-autocomplete
              label="Lauko tipas"
              v-model="form.field_type"
              :items="enumsClassifiers.actionExtraFieldType"
              enums
              itemvalue="value"
              itemtext="text"
              required
            />
          </v-col>
          <v-col v-if="isManualFieldType" cols="12" md="4" class="pb-0">
            <el-autocomplete
              label="Leidžiama įvesti"
              v-model="form.manual_input_type"
              :items="enumsClassifiers.actionExtraFieldManualInputType"
              enums
              itemvalue="value"
              itemtext="text"
              required
            />
          </v-col>
          <v-col cols="12" class="pb-0">
            <el-text-field
              label="Pavadinimas (LT)"
              v-model="form.label_lt"
              :maxlength="500"
              required
            />
          </v-col>
          <v-col cols="12" class="pb-0">
            <el-text-field
              label="Pavadinimas (EN)"
              v-model="form.label_en"
              :maxlength="500"
              required
            />
          </v-col>
          <v-col cols="12" class="pb-0">
            <el-textarea
              label="Tooltip tekstas (LT)"
              v-model="form.description_lt"
              :maxlength="2000"
            />
          </v-col>
          <v-col cols="12" class="pb-0">
            <el-textarea
              label="Tooltip tekstas (EN)"
              v-model="form.description_en"
              :maxlength="2000"
            />
          </v-col>
        </v-row>
        </form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <el-button
          title="Atšaukti"
          color="secondary"
          @click.native.stop="close()"
          :loading="saving"
        />
        <el-button
          title="Išsaugoti"
          color="primary"
          icon="išsaugoti"
          @click.native.stop="submit()"
          :loading="saving"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Enums from '@/enums'

const emptyForm = () => ({
  field_key: '',
  field_type: '',
  manual_input_type: '',
  label_lt: '',
  label_en: '',
  description_lt: '',
  description_en: '',
})

const FIELD_TYPE_VALUES = ['manual', 'ntr']
const MANUAL_INPUT_TYPE_VALUES = ['numbers', 'letters', 'numbers_and_letters']
/** Lowercase letter first; then letters, digits, underscore only. */
const FIELD_KEY_PATTERN = /^[a-z]\w*$/

export default {
  name: 'ModalActionExtraField',
  $_veeValidate: {
    validator: 'new',
  },
  props: {
    value: Boolean,
    setId: {
      type: [Number, String],
      required: true,
    },
    field: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      saving: false,
      form: emptyForm(),
      editId: null,
      enumsClassifiers: Enums,
    }
  },
  computed: {
    isManualFieldType() {
      return this.form.field_type === 'manual'
    },
    dialog: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.syncForm()
        this.$nextTick(() => {
          if (this.$validator) {
            this.$validator.reset()
          }
        })
      }
    },
    field: {
      handler() {
        if (this.value) {
          this.syncForm()
        }
      },
      deep: true,
    },
    'form.field_type'(val) {
      if (val !== 'manual') {
        this.$set(this.form, 'manual_input_type', '')
      }
    },
  },
  methods: {
    syncForm() {
      if (this.field && this.field.id) {
        this.editId = this.field.id
        this.form = {
          field_key: this.field.field_key || '',
          field_type: this.field.field_type || '',
          manual_input_type:
            this.field.field_type === 'ntr'
              ? ''
              : this.field.manual_input_type || '',
          label_lt: this.field.label_lt || '',
          label_en: this.field.label_en || '',
          description_lt: this.field.description_lt || '',
          description_en: this.field.description_en || '',
        }
      } else {
        this.editId = null
        this.form = emptyForm()
      }
    },
    close() {
      if (this.$validator) {
        this.$validator.reset()
      }
      this.dialog = false
    },
    submit() {
      this.$validator.validateAll().then((res) => {
        if (!res) {
          this.$root.$emit('FORMAT_ERROR')
          return
        }
        const keyTrim = this.form.field_key.trim()
        if (!keyTrim) {
          this.$root.$emit(
            'DATA_ERROR_DIRECT_TEXT',
            'Užpildykite privalomą lauką: lauko raktas'
          )
          return
        }
        if (!FIELD_KEY_PATTERN.test(keyTrim)) {
          this.$root.$emit(
            'DATA_ERROR_DIRECT_TEXT',
            this.$t(
              'Lauko raktas turi prasidėti mažąja raide ir gali būti sudarytas tik iš raidžių, skaičių ir pabraukimo ženklo (_).'
            )
          )
          return
        }
        if (!FIELD_TYPE_VALUES.includes(this.form.field_type)) {
          this.$root.$emit(
            'DATA_ERROR_DIRECT_TEXT',
            'Pasirinkite lauko tipą iš sąrašo'
          )
          return
        }
        if (this.form.field_type === 'manual') {
          if (!MANUAL_INPUT_TYPE_VALUES.includes(this.form.manual_input_type)) {
            this.$root.$emit(
              'DATA_ERROR_DIRECT_TEXT',
              'Pasirinkite Leidžiama įvesti'
            )
            return
          }
        }
        this.saving = true
        const manualInput =
          this.form.field_type === 'ntr' ? null : this.form.manual_input_type || null
        const payload = {
          id: this.editId,
          action_extra_field_set_id: Number(this.setId),
          field_key: keyTrim,
          field_type: this.form.field_type,
          manual_input_type: manualInput,
          label_lt: this.form.label_lt || null,
          label_en: this.form.label_en || null,
          description_lt: this.form.description_lt || null,
          description_en: this.form.description_en || null,
        }
        this.$axios
          .post(`action-extra-fields`, payload)
          .then(() => {
            this.$root.$emit('DATA_OK_CODE', '000110')
            this.$emit('saved')
            this.close()
          })
          .catch((err) => {
            this.$root.$emit('DATA_ERROR', err)
          })
          .finally(() => {
            this.saving = false
          })
      })
    },
  },
}
</script>
